import { readProjectSettings } from '../admin/adminSettings';
import { readAdminSession } from '../admin/authProvider';
import { type ExamName, type ExamTaskCategory } from '../examTasks/examTaskTypes';

export type MarkingTaskRuleKind = 'Objective' | 'ComponentBased';
export type MarkingComponentRuleKind =
    | 'AbsoluteBands'
    | 'RelativeBands'
    | 'WordCountBands'
    | 'ErrorCountBands'
    | 'FixedScore'
    | 'FixedScoreWhenAllGatesPass'
    | 'CombinedGrammarPenalty'
    | 'AppGrammarMetric'
    | 'EmailConventionsMetric'
    | 'OrganizationMetric';
export type RelativeThresholdKind =
    | 'Absolute'
    | 'ReferenceCountMinus'
    | 'FloorOfReferenceCountMinusOffsetDividedByDivisor';
export type AppGrammarMetricKind = 'Grammar' | 'Vocabulary' | 'LinguisticRange' | 'Structure' | 'Spelling';
export type MockTestConcessionKind =
    | 'SubtractCount'
    | 'SubtractHalfRounded'
    | 'SubtractThirdRounded'
    | 'SubtractQuarterRounded'
    | 'SubtractCountMinusOne'
    | 'SubtractDoubleCount'
    | 'SubtractHalfCount'
    | 'SubtractHalfFloored';
export type MockTestScoreMeasureKind = 'Total' | 'ContentOnly';
export type MarkTitleValue =
    | 'Content'
    | 'Fluency'
    | 'Pronunciation'
    | 'Total'
    | 'Score'
    | 'Spelling'
    | 'Vocabulary'
    | 'Form'
    | 'Grammar'
    | 'LinguisticRange'
    | 'Structure'
    | 'EmailConventions'
    | 'Organization';

export type ThresholdScoreBand = {
    minInclusive: number | null;
    maxInclusive: number | null;
    score: number;
};

export type WordCountScoreBand = {
    minInclusive: number;
    maxInclusive: number;
    score: number;
};

export type RelativeThresholdScoreBand = {
    thresholdKind: RelativeThresholdKind;
    thresholdValue: number;
    offset: number;
    divisor: number;
    score: number;
};

export type ObjectiveMarkingConfig = {
    correctAnswerScore: number;
    wrongAnswerPenalty: number;
    minimumScore: number;
    singleChoiceCorrectScore: number;
    reorderAdjacentPairScore: number;
};

export type MarkingComponentConfig = {
    title: MarkTitleValue;
    outOf: number;
    ruleKind: MarkingComponentRuleKind;
    requiresPositiveContentScore: boolean;
    requiresPositiveFormScore: boolean;
    fixedScore: number;
    penaltyPerError: number;
    roundDecimals: number;
    appGrammarMetric: AppGrammarMetricKind | null;
    thresholdBands: ThresholdScoreBand[];
    wordCountBands: WordCountScoreBand[];
    relativeThresholdBands: RelativeThresholdScoreBand[];
};

export type SpeechAnalysisFluencyRubricConfig = {
    pauseDisplayMinSeconds: number;
    freeBriefPauseCount: number;
    briefPauseMaxSeconds: number;
    longPauseMinSeconds: number;
    freeLongPauseCount: number;
    longPauseMaxSeconds: number;
    longPauseCountPenalty: number;
    veryLongPauseCountPenalty: number;
    punctuationPauseMaxSeconds: number | null;
};

export type SpeechAnalysisPronunciationRubricConfig = {
    excludeLowConfidenceWords: boolean;
    minWhisperWordScore: number;
    minimumReliableWords: number;
};

export type SpeechAnalysisRubricConfig = {
    rubricVersion: string;
    fluency: SpeechAnalysisFluencyRubricConfig;
    pronunciation: SpeechAnalysisPronunciationRubricConfig;
};

export type TaskMarkingConfig = {
    examName: ExamName;
    examTaskType: string;
    ruleKind: MarkingTaskRuleKind;
    includeTotalRow: boolean;
    objective: ObjectiveMarkingConfig | null;
    semanticScoring: unknown;
    speechAnalysis: SpeechAnalysisRubricConfig | null;
    components: MarkingComponentConfig[];
};

export type MockTestSectionAllocationConfig = {
    examName: ExamName | null;
    sectionCategory: ExamTaskCategory;
    examTaskType: string;
    allocatedMark: number;
    concessionKind: MockTestConcessionKind;
    measureKind: MockTestScoreMeasureKind;
};

export type MockTestScoringConfiguration = {
    normalizedScoreOutOf: number;
    minimumNormalizedScore: number;
    maximumNormalizedScore: number;
    sectionAllocationRules: MockTestSectionAllocationConfig[];
};

export type AdminTaskMarkingConfigDTO = {
    markingTaskConfigId: string;
    examName: ExamName;
    examTaskType: string;
    configuration: TaskMarkingConfig;
    createdAt: string;
    updatedAt: string;
};

export type AdminMockTestScoringConfigDTO = {
    mockTestScoringConfigId: string;
    configuration: MockTestScoringConfiguration;
    createdAt: string;
    updatedAt: string;
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export const markingTaskRuleKinds: MarkingTaskRuleKind[] = ['Objective', 'ComponentBased'];
export const markingComponentRuleKinds: MarkingComponentRuleKind[] = [
    'AbsoluteBands',
    'RelativeBands',
    'WordCountBands',
    'ErrorCountBands',
    'FixedScore',
    'FixedScoreWhenAllGatesPass',
    'CombinedGrammarPenalty',
    'AppGrammarMetric',
    'EmailConventionsMetric',
    'OrganizationMetric',
];
export const relativeThresholdKinds: RelativeThresholdKind[] = [
    'Absolute',
    'ReferenceCountMinus',
    'FloorOfReferenceCountMinusOffsetDividedByDivisor',
];
export const appGrammarMetricKinds: AppGrammarMetricKind[] = [
    'Grammar',
    'Vocabulary',
    'LinguisticRange',
    'Structure',
    'Spelling',
];
export const mockTestConcessionKinds: MockTestConcessionKind[] = [
    'SubtractCount',
    'SubtractHalfRounded',
    'SubtractThirdRounded',
    'SubtractQuarterRounded',
    'SubtractCountMinusOne',
    'SubtractDoubleCount',
    'SubtractHalfCount',
    'SubtractHalfFloored',
];
export const mockTestScoreMeasureKinds: MockTestScoreMeasureKind[] = ['Total', 'ContentOnly'];
export const markTitleValues: MarkTitleValue[] = [
    'Content',
    'Fluency',
    'Pronunciation',
    'Total',
    'Score',
    'Spelling',
    'Vocabulary',
    'Form',
    'Grammar',
    'LinguisticRange',
    'Structure',
    'EmailConventions',
    'Organization',
];

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Marking configuration request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

function asObject(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }

    return {};
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function asExamName(value: unknown, fallback: ExamName = 'Academic'): ExamName {
    return value === 'Core' ? 'Core' : fallback;
}

function asNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
}

function asNullableNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    return null;
}

function asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
}

function normalizeThresholdBand(value: unknown): ThresholdScoreBand {
    const record = asObject(value);

    return {
        minInclusive: asNullableNumber(record.minInclusive ?? record.MinInclusive),
        maxInclusive: asNullableNumber(record.maxInclusive ?? record.MaxInclusive),
        score: asNumber(record.score ?? record.Score, 0),
    };
}

function normalizeWordCountBand(value: unknown): WordCountScoreBand {
    const record = asObject(value);

    return {
        minInclusive: asNumber(record.minInclusive ?? record.MinInclusive, 0),
        maxInclusive: asNumber(record.maxInclusive ?? record.MaxInclusive, 0),
        score: asNumber(record.score ?? record.Score, 0),
    };
}

function normalizeRelativeThresholdBand(value: unknown): RelativeThresholdScoreBand {
    const record = asObject(value);

    return {
        thresholdKind: asString(record.thresholdKind ?? record.ThresholdKind, 'Absolute') as RelativeThresholdKind,
        thresholdValue: asNumber(record.thresholdValue ?? record.ThresholdValue, 0),
        offset: asNumber(record.offset ?? record.Offset, 0),
        divisor: asNumber(record.divisor ?? record.Divisor, 1),
        score: asNumber(record.score ?? record.Score, 0),
    };
}

function normalizeObjectiveMarkingConfig(value: unknown): ObjectiveMarkingConfig {
    const record = asObject(value);

    return {
        correctAnswerScore: asNumber(record.correctAnswerScore ?? record.CorrectAnswerScore, 1),
        wrongAnswerPenalty: asNumber(record.wrongAnswerPenalty ?? record.WrongAnswerPenalty, 0),
        minimumScore: asNumber(record.minimumScore ?? record.MinimumScore, 0),
        singleChoiceCorrectScore: asNumber(record.singleChoiceCorrectScore ?? record.SingleChoiceCorrectScore, 1),
        reorderAdjacentPairScore: asNumber(record.reorderAdjacentPairScore ?? record.ReorderAdjacentPairScore, 1),
    };
}

function normalizeMarkingComponentConfig(value: unknown): MarkingComponentConfig {
    const record = asObject(value);

    return {
        title: asString(record.title ?? record.Title, 'Content') as MarkTitleValue,
        outOf: asNumber(record.outOf ?? record.OutOf, 0),
        ruleKind: asString(record.ruleKind ?? record.RuleKind, 'FixedScore') as MarkingComponentRuleKind,
        requiresPositiveContentScore: asBoolean(
            record.requiresPositiveContentScore ?? record.RequiresPositiveContentScore,
            false,
        ),
        requiresPositiveFormScore: asBoolean(record.requiresPositiveFormScore ?? record.RequiresPositiveFormScore, false),
        fixedScore: asNumber(record.fixedScore ?? record.FixedScore, 0),
        penaltyPerError: asNumber(record.penaltyPerError ?? record.PenaltyPerError, 0),
        roundDecimals: asNumber(record.roundDecimals ?? record.RoundDecimals, 1),
        appGrammarMetric: (asString(record.appGrammarMetric ?? record.AppGrammarMetric, '') || null) as
            | AppGrammarMetricKind
            | null,
        thresholdBands: asArray(record.thresholdBands ?? record.ThresholdBands).map(normalizeThresholdBand),
        wordCountBands: asArray(record.wordCountBands ?? record.WordCountBands).map(normalizeWordCountBand),
        relativeThresholdBands: asArray(record.relativeThresholdBands ?? record.RelativeThresholdBands).map(
            normalizeRelativeThresholdBand,
        ),
    };
}

function normalizeSpeechAnalysisRubricConfig(value: unknown): SpeechAnalysisRubricConfig {
    const record = asObject(value);
    const fluency = asObject(record.fluency ?? record.Fluency);
    const pronunciation = asObject(record.pronunciation ?? record.Pronunciation);
    const punctuationPauseValue = Object.prototype.hasOwnProperty.call(fluency, 'punctuationPauseMaxSeconds')
        ? fluency.punctuationPauseMaxSeconds
        : Object.prototype.hasOwnProperty.call(fluency, 'PunctuationPauseMaxSeconds')
          ? fluency.PunctuationPauseMaxSeconds
          : 1;
    const longPauseCountPenalty = asNumber(
        fluency.longPauseCountPenalty ?? fluency.LongPauseCountPenalty,
        -1,
    );

    return {
        rubricVersion: asString(record.rubricVersion ?? record.RubricVersion, '2026-01'),
        fluency: {
            pauseDisplayMinSeconds: asNumber(
                fluency.pauseDisplayMinSeconds ?? fluency.PauseDisplayMinSeconds,
                0.25,
            ),
            freeBriefPauseCount: asNumber(fluency.freeBriefPauseCount ?? fluency.FreeBriefPauseCount, 4),
            briefPauseMaxSeconds: asNumber(fluency.briefPauseMaxSeconds ?? fluency.BriefPauseMaxSeconds, 0.8),
            longPauseMinSeconds: asNumber(fluency.longPauseMinSeconds ?? fluency.LongPauseMinSeconds, 1),
            freeLongPauseCount: asNumber(fluency.freeLongPauseCount ?? fluency.FreeLongPauseCount, 2),
            longPauseMaxSeconds: asNumber(fluency.longPauseMaxSeconds ?? fluency.LongPauseMaxSeconds, 1.2),
            longPauseCountPenalty,
            veryLongPauseCountPenalty: asNumber(
                fluency.veryLongPauseCountPenalty ?? fluency.VeryLongPauseCountPenalty,
                longPauseCountPenalty,
            ),
            punctuationPauseMaxSeconds:
                punctuationPauseValue === null ? null : asNumber(punctuationPauseValue, 1),
        },
        pronunciation: {
            excludeLowConfidenceWords: asBoolean(
                pronunciation.excludeLowConfidenceWords ?? pronunciation.ExcludeLowConfidenceWords,
                true,
            ),
            minWhisperWordScore: asNumber(
                pronunciation.minWhisperWordScore ?? pronunciation.MinWhisperWordScore,
                0.6,
            ),
            minimumReliableWords: asNumber(
                pronunciation.minimumReliableWords ?? pronunciation.MinimumReliableWords,
                3,
            ),
        },
    };
}

function normalizeTaskMarkingConfig(value: unknown): TaskMarkingConfig {
    const record = asObject(value);
    const speechAnalysis = record.speechAnalysis ?? record.SpeechAnalysis;

    return {
        examName: asExamName(record.examName ?? record.ExamName),
        examTaskType: asString(record.examTaskType ?? record.ExamTaskType),
        ruleKind: asString(record.ruleKind ?? record.RuleKind, 'Objective') as MarkingTaskRuleKind,
        includeTotalRow: asBoolean(record.includeTotalRow ?? record.IncludeTotalRow, true),
        objective: record.objective ?? record.Objective ? normalizeObjectiveMarkingConfig(record.objective ?? record.Objective) : null,
        semanticScoring: record.semanticScoring ?? record.SemanticScoring ?? null,
        speechAnalysis: speechAnalysis ? normalizeSpeechAnalysisRubricConfig(speechAnalysis) : null,
        components: asArray(record.components ?? record.Components).map(normalizeMarkingComponentConfig),
    };
}

function normalizeMockTestSectionAllocationConfig(value: unknown): MockTestSectionAllocationConfig {
    const record = asObject(value);
    const examNameValue = record.examName ?? record.ExamName;

    return {
        examName: examNameValue === 'Academic' || examNameValue === 'Core' ? examNameValue : null,
        sectionCategory: asString(record.sectionCategory ?? record.SectionCategory, 'Speaking') as ExamTaskCategory,
        examTaskType: asString(record.examTaskType ?? record.ExamTaskType),
        allocatedMark: asNumber(record.allocatedMark ?? record.AllocatedMark, 0),
        concessionKind: asString(record.concessionKind ?? record.ConcessionKind, 'SubtractCount') as MockTestConcessionKind,
        measureKind: asString(record.measureKind ?? record.MeasureKind, 'Total') as MockTestScoreMeasureKind,
    };
}

function normalizeMockTestScoringConfiguration(value: unknown): MockTestScoringConfiguration {
    const record = asObject(value);

    return {
        normalizedScoreOutOf: asNumber(record.normalizedScoreOutOf ?? record.NormalizedScoreOutOf, 90),
        minimumNormalizedScore: asNumber(record.minimumNormalizedScore ?? record.MinimumNormalizedScore, 10),
        maximumNormalizedScore: asNumber(record.maximumNormalizedScore ?? record.MaximumNormalizedScore, 90),
        sectionAllocationRules: asArray(record.sectionAllocationRules ?? record.SectionAllocationRules).map(
            normalizeMockTestSectionAllocationConfig,
        ),
    };
}

function normalizeAdminTaskMarkingConfigDTO(value: unknown): AdminTaskMarkingConfigDTO {
    const record = asObject(value);

    return {
        markingTaskConfigId: asString(record.markingTaskConfigId ?? record.MarkingTaskConfigId),
        examName: asExamName(record.examName ?? record.ExamName),
        examTaskType: asString(record.examTaskType ?? record.ExamTaskType),
        configuration: normalizeTaskMarkingConfig(record.configuration ?? record.Configuration),
        createdAt: asString(record.createdAt ?? record.CreatedAt),
        updatedAt: asString(record.updatedAt ?? record.UpdatedAt),
    };
}

function normalizeAdminMockTestScoringConfigDTO(value: unknown): AdminMockTestScoringConfigDTO {
    const record = asObject(value);

    return {
        mockTestScoringConfigId: asString(record.mockTestScoringConfigId ?? record.MockTestScoringConfigId),
        configuration: normalizeMockTestScoringConfiguration(record.configuration ?? record.Configuration),
        createdAt: asString(record.createdAt ?? record.CreatedAt),
        updatedAt: asString(record.updatedAt ?? record.UpdatedAt),
    };
}

async function requestAdminMarking<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const requestPath = trimSlashes(path);
    const url = new URL(requestPath ? `${apiRoot}/AdminMarking/${requestPath}` : `${apiRoot}/AdminMarking`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Marking configuration request failed'));
        }
    }

    return payload as T;
}

function unwrapList<T>(payload: unknown): T[] {
    const unwrapped = unwrapAdminApiPayload(payload as T[] | AdminApiEnvelope<T[]>);

    if (Array.isArray(unwrapped)) {
        return unwrapped;
    }

    if (unwrapped && typeof unwrapped === 'object') {
        const record = unwrapped as Record<string, unknown>;
        if (Array.isArray(record.items)) {
            return record.items as T[];
        }
        if (Array.isArray(record.data)) {
            return record.data as T[];
        }
    }

    return [];
}

export function createDefaultObjectiveMarkingConfig(): ObjectiveMarkingConfig {
    return {
        correctAnswerScore: 1,
        wrongAnswerPenalty: 0,
        minimumScore: 0,
        singleChoiceCorrectScore: 1,
        reorderAdjacentPairScore: 1,
    };
}

export function createDefaultMarkingComponentConfig(title: MarkTitleValue = 'Content'): MarkingComponentConfig {
    return {
        title,
        outOf: 1,
        ruleKind: 'FixedScore',
        requiresPositiveContentScore: false,
        requiresPositiveFormScore: false,
        fixedScore: 1,
        penaltyPerError: 0,
        roundDecimals: 1,
        appGrammarMetric: null,
        thresholdBands: [],
        wordCountBands: [],
        relativeThresholdBands: [],
    };
}

export function createDefaultSpeechAnalysisRubricConfig(): SpeechAnalysisRubricConfig {
    return {
        rubricVersion: '2026-01',
        fluency: {
            pauseDisplayMinSeconds: 0.25,
            freeBriefPauseCount: 4,
            briefPauseMaxSeconds: 0.8,
            longPauseMinSeconds: 1,
            freeLongPauseCount: 2,
            longPauseMaxSeconds: 1.2,
            longPauseCountPenalty: -1,
            veryLongPauseCountPenalty: -1,
            punctuationPauseMaxSeconds: 1,
        },
        pronunciation: {
            excludeLowConfidenceWords: true,
            minWhisperWordScore: 0.6,
            minimumReliableWords: 3,
        },
    };
}

export function createDefaultTaskMarkingConfig(
    examName: ExamName,
    examTaskType: string,
    ruleKind: MarkingTaskRuleKind,
): TaskMarkingConfig {
    return {
        examName,
        examTaskType,
        ruleKind,
        includeTotalRow: true,
        objective: createDefaultObjectiveMarkingConfig(),
        semanticScoring: null,
        speechAnalysis: null,
        components: ruleKind === 'ComponentBased' ? [createDefaultMarkingComponentConfig()] : [],
    };
}

export function createDefaultMockTestSectionAllocationConfig(
    examName: ExamName,
    sectionCategory: ExamTaskCategory,
    examTaskType: string,
): MockTestSectionAllocationConfig {
    return {
        examName,
        sectionCategory,
        examTaskType,
        allocatedMark: 1,
        concessionKind: 'SubtractCount',
        measureKind: 'Total',
    };
}

export function createDefaultMockTestScoringConfiguration(
    sectionAllocationRules: MockTestSectionAllocationConfig[],
): MockTestScoringConfiguration {
    return {
        normalizedScoreOutOf: 90,
        minimumNormalizedScore: 10,
        maximumNormalizedScore: 90,
        sectionAllocationRules,
    };
}

export async function getAdminMarkingTaskConfigs(params?: {
    examName?: ExamName;
    examTaskType?: string;
}): Promise<AdminTaskMarkingConfigDTO[]> {
    const payload = await requestAdminMarking<unknown>('task-configs', { method: 'GET' }, {
        ...(params?.examName ? { examName: params.examName } : {}),
        ...(params?.examTaskType ? { examTaskType: params.examTaskType } : {}),
    });

    return unwrapList<unknown>(payload).map(normalizeAdminTaskMarkingConfigDTO);
}

export async function getAdminMarkingTaskConfig(
    examName: ExamName,
    examTaskType: string,
): Promise<AdminTaskMarkingConfigDTO | null> {
    try {
        const payload = await requestAdminMarking<unknown>(`task-configs/${examName}/${examTaskType}`, { method: 'GET' });
        return normalizeAdminTaskMarkingConfigDTO(unwrapAdminApiPayload(payload as AdminTaskMarkingConfigDTO | AdminApiEnvelope<AdminTaskMarkingConfigDTO>));
    } catch (error) {
        if (error instanceof Error && error.message.toLowerCase().includes('not found')) {
            return null;
        }

        throw error;
    }
}

export async function upsertAdminMarkingTaskConfig(
    examName: ExamName,
    examTaskType: string,
    configuration: TaskMarkingConfig,
): Promise<AdminTaskMarkingConfigDTO> {
    const payload = await requestAdminMarking<unknown>(`task-configs/${examName}/${examTaskType}`, {
        method: 'PUT',
        body: JSON.stringify({ configuration }),
    });

    return normalizeAdminTaskMarkingConfigDTO(unwrapAdminApiPayload(payload as AdminTaskMarkingConfigDTO | AdminApiEnvelope<AdminTaskMarkingConfigDTO>));
}

export async function getAdminMockTestScoringConfig(): Promise<AdminMockTestScoringConfigDTO | null> {
    try {
        const payload = await requestAdminMarking<unknown>('mock-test-scoring', { method: 'GET' });
        return normalizeAdminMockTestScoringConfigDTO(unwrapAdminApiPayload(payload as AdminMockTestScoringConfigDTO | AdminApiEnvelope<AdminMockTestScoringConfigDTO>));
    } catch (error) {
        if (error instanceof Error && error.message.toLowerCase().includes('not found')) {
            return null;
        }

        throw error;
    }
}

export async function upsertAdminMockTestScoringConfig(
    configuration: MockTestScoringConfiguration,
): Promise<AdminMockTestScoringConfigDTO> {
    const payload = await requestAdminMarking<unknown>('mock-test-scoring', {
        method: 'PUT',
        body: JSON.stringify({ configuration }),
    });

    return normalizeAdminMockTestScoringConfigDTO(unwrapAdminApiPayload(payload as AdminMockTestScoringConfigDTO | AdminApiEnvelope<AdminMockTestScoringConfigDTO>));
}
